import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { TimerContext } from './TimerContext';

interface TimerContextType {
    initialTime: number;
    setInitialTime: (time: number) => void;
    time: number;
    isRunning: boolean;
    startTimer: () => void;
    stopTimer: () => void;
    resetTimer: () => void;
    setTime: (time: number) => void;
    formatTime: (seconds: number) => string;
    removeTime: (secondsToRemove: number) => void;
}

export const TimerProvider = ({ children }: { children: ReactNode }) => {
    const [initialTime, setInitialTime] = useState<number>(20 * 60); // Default to 20 minutes
    const [time, setTimeState] = useState<number>(initialTime);
    const [isRunning, setIsRunning] = useState<boolean>(false);

    useEffect(() => {
        let interval: number | null = null;

        if (isRunning) {
            interval = setInterval(() => {
                setTimeState((prev) => {
                    if (prev <= 1) {
                        setIsRunning(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isRunning]);

    const value: TimerContextType = {
        initialTime,
        setInitialTime: setInitialTime,
        time,
        isRunning,
        startTimer: () => setIsRunning(true),
        stopTimer: () => setIsRunning(false),
        resetTimer: () => {
            setTimeState(20 * 60);
            setIsRunning(false);
        },
        setTime: (t) => setTimeState(t),
        formatTime: (s) =>
            `${Math.floor(s / 60)
                .toString()
                .padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`,
        removeTime: (sec) => setTimeState((prev) => Math.max(prev - sec, 0)),
    };

    return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};
